// components/LinkFollowModal/index.tsx
// フォロー紐づけ用フォロー選択モーダル
//
// 機能概要:
//   - 既存フォローを検索・選択し、別のフォローと紐づける
//   - 検索条件: 会社名・顧客名・担当者
//   - 一覧: チェックボックス選択、フォロー日付ソート
//   - 選択件数をボタンに表示し、紐づけを確定する
//

import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Input, Select, Checkbox, Tag, Spin, message } from 'antd';
import {
  SearchOutlined,
  LinkOutlined,
  UpOutlined,
  DownOutlined,
  SwapOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { MODAL_Z } from '../../utils/modalZIndex';

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------

export interface LinkFollowItem {
  id: number;
  followNo: string;
  followDate: string;    // YYYY-MM-DD
  companyName: string;
  customerName: string;
  assignee: string;
  assigneeUserno: number;
  content: string;
  isOnsite: boolean;
  /** 所属する関連フォロー群No（未所属は null）。群単位の連動選択・マージ振り分けに使用 */
  relationFollowGroupNo: number | null;
}

export interface LinkFollowModalProps {
  open: boolean;
  onCancel: () => void;
  /**
   * 紐づけ確定時のコールバック。
   * 選択候補を「群未所属（followNos）」「群所属（groupNos）」に振り分けて渡す（Web版と同一仕様）。
   */
  onLink: (payload: { followNos: number[]; groupNos: number[] }) => void;
  /** 担当者フィルター用ユーザー一覧 */
  contactUsers: { userno: number; username: string }[];
  /** 初期検索の会社名（紐づけ元フォローの会社名でシード。Web版の顧客初期条件相当） */
  initialCompanyName?: string;
  /** 初期検索の顧客名（紐づけ元フォローの顧客名でシード） */
  initialCustomerName?: string;
  /** 紐づけ対象フォローの検索コールバック（サーバー検索） */
  onSearch: (params: {
    companyName?: string;
    customerName?: string;
    assigneeUserno?: number | null;
  }) => Promise<LinkFollowItem[]>;
  zIndex?: number;
}

// ----------------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------------

const LinkFollowModal: React.FC<LinkFollowModalProps> = ({
  open,
  onCancel,
  onLink,
  contactUsers,
  onSearch,
  zIndex = MODAL_Z.L3,
  initialCompanyName,
  initialCustomerName,
}) => {
  // 検索フォーム
  const [companyName, setCompanyName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [assigneeUserno, setAssigneeUserno] = useState<number | null>(null);
  const [searchCollapsed, setSearchCollapsed] = useState(true);

  // 一覧
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  // 検索結果（サーバー検索）
  const [results, setResults] = useState<LinkFollowItem[]>([]);
  const [searching, setSearching] = useState(false);

  // 検索実行（サーバー検索）。選択状態は結果が変わるためクリアする。
  const runSearch = async (conditions: { companyName?: string; customerName?: string; assigneeUserno?: number | null }) => {
    setSearching(true);
    try {
      const list = await onSearch(conditions);
      setResults(list);
      setCheckedIds(new Set());
    } catch {
      setResults([]);
      message.error('紐づけ候補の検索に失敗しました');
    } finally {
      setSearching(false);
    }
  };

  // モーダルを開くたびに条件・選択をリセットし、紐づけ元の会社名/顧客名でシードして初期検索する
  useEffect(() => {
    if (!open) return;
    const seedCompany = initialCompanyName ?? '';
    const seedCustomer = initialCustomerName ?? '';
    setCompanyName(seedCompany);
    setCustomerName(seedCustomer);
    setAssigneeUserno(null);
    setCheckedIds(new Set());
    setSortOrder('desc');
    runSearch({
      companyName: seedCompany || undefined,
      customerName: seedCustomer || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // モーダルを閉じる際のリセット
  const handleCancel = () => {
    setCompanyName('');
    setCustomerName('');
    setAssigneeUserno(null);
    setCheckedIds(new Set());
    setSortOrder('desc');
    onCancel();
  };

  const handleClear = () => {
    setCompanyName('');
    setCustomerName('');
    setAssigneeUserno(null);
  };

  // サーバー検索結果をそのまま表示（クライアント再フィルタはしない）。ソートのみクライアントで行う。
  const filteredItems = useMemo(() => {
    return [...results].sort((a, b) => {
      const diff = a.followDate.localeCompare(b.followDate);
      return sortOrder === 'desc' ? -diff : diff;
    });
  }, [results, sortOrder]);

  // 同一群のフォローは連動してチェック/解除する（Web版 addFollowLink.js 相当）
  const toggleCheck = (id: number) => {
    const target = results.find(r => r.id === id);
    const groupNo = target?.relationFollowGroupNo ?? null;
    setCheckedIds(prev => {
      const next = new Set(prev);
      const willCheck = !next.has(id);
      const targetIds = groupNo != null
        ? results.filter(r => r.relationFollowGroupNo === groupNo).map(r => r.id)
        : [id];
      targetIds.forEach(i => { willCheck ? next.add(i) : next.delete(i); });
      return next;
    });
  };

  const allChecked = filteredItems.length > 0 && filteredItems.every(i => checkedIds.has(i.id));
  const someChecked = filteredItems.some(i => checkedIds.has(i.id)) && !allChecked;

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(prev => {
        const next = new Set(prev);
        filteredItems.forEach(i => next.delete(i.id));
        return next;
      });
    } else {
      setCheckedIds(prev => {
        const next = new Set(prev);
        filteredItems.forEach(i => next.add(i.id));
        return next;
      });
    }
  };

  const handleLink = () => {
    // チェック済みのうち検索結果内のものを、未所属(followNos)/群所属(groupNos)に振り分ける
    const followNos: number[] = [];
    const groupNos = new Set<number>();
    results.forEach(r => {
      if (!checkedIds.has(r.id)) return;
      if (r.relationFollowGroupNo != null) {
        groupNos.add(r.relationFollowGroupNo);
      } else {
        followNos.push(r.id);
      }
    });
    onLink({ followNos, groupNos: Array.from(groupNos) });
    handleCancel();
  };

  return (
    <>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: zIndex - 1 }} />
      )}
      <Modal
      title="フォローを紐づける"
      open={open}
      onCancel={handleCancel}
      width={560}
      centered
      zIndex={zIndex}
      mask={false}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '75vh' },
      }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
            {checkedIds.size > 0 ? `${checkedIds.size}件選択中` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" onClick={handleCancel}>閉じる</Button>
            <Button
              type="primary"
              size="small"
              icon={<LinkOutlined />}
              disabled={checkedIds.size === 0}
              onClick={handleLink}
            >
              {checkedIds.size > 0 ? `紐づける（${checkedIds.size}件）` : '紐づける'}
            </Button>
          </div>
        </div>
      }
    >
      {/* ── 検索セクション ── */}
      <div style={{ flexShrink: 0, background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
        {/* 検索セクションヘッダー（開閉トグル） */}
        <button
          onClick={() => setSearchCollapsed(v => !v)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 'bold',
            color: '#595959',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <SearchOutlined style={{ fontSize: 13 }} />
            検索条件
          </span>
          {searchCollapsed ? <DownOutlined style={{ fontSize: 11 }} /> : <UpOutlined style={{ fontSize: 11 }} />}
        </button>

        {/* 検索フォーム（折りたたみ） */}
        {!searchCollapsed && (
          <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>会社名</div>
                <Input
                  size="small"
                  placeholder="会社名で絞り込み"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  allowClear
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>顧客名</div>
                <Input
                  size="small"
                  placeholder="顧客名で絞り込み"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  allowClear
                />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>担当者</div>
              <Select
                size="small"
                style={{ width: '100%' }}
                placeholder="担当者を選択"
                allowClear
                value={assigneeUserno}
                onChange={v => setAssigneeUserno(v ?? null)}
                options={contactUsers.map(u => ({ value: u.userno, label: u.username }))}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={handleClear}>クリア</Button>
              <Button
                size="small"
                type="primary"
                icon={<SearchOutlined />}
                loading={searching}
                onClick={() => runSearch({ companyName, customerName, assigneeUserno })}
              >
                検索
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── 一覧ヘッダー（件数・全選択・ソート） ── */}
      <div style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderBottom: '1px solid #f0f0f0',
        background: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={toggleAll}
          />
          <span style={{ fontSize: 12, color: '#595959' }}>
            全<span style={{ fontWeight: 'bold', color: '#262626' }}>{filteredItems.length}</span>件
          </span>
        </div>
        <button
          onClick={() => setSortOrder(v => v === 'desc' ? 'asc' : 'desc')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'none',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: 12,
            color: '#595959',
          }}
        >
          <SwapOutlined style={{ fontSize: 11 }} />
          フォロー日
          {sortOrder === 'desc' ? <DownOutlined style={{ fontSize: 10 }} /> : <UpOutlined style={{ fontSize: 10 }} />}
        </button>
      </div>

      {/* ── フォロー一覧（スクロール対象） ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {searching ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#bfbfbf', fontSize: 13 }}>
            該当するフォローがありません
          </div>
        ) : (
          filteredItems.map((item, idx) => (
            <div
              key={item.id}
              onClick={() => toggleCheck(item.id)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 16px',
                borderBottom: idx < filteredItems.length - 1 ? '1px solid #f5f5f5' : 'none',
                background: checkedIds.has(item.id) ? '#e6f4ff' : '#fff',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {/* チェックボックス */}
              <div style={{ paddingTop: 2, flexShrink: 0 }}>
                <Checkbox
                  checked={checkedIds.has(item.id)}
                  onChange={() => toggleCheck(item.id)}
                  onClick={e => e.stopPropagation()}
                />
              </div>

              {/* コンテンツ */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 1行目: 日付 + 訪問/来社 + 担当者 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 'bold', color: checkedIds.has(item.id) ? '#1677ff' : '#262626' }}>
                    {item.followDate}
                  </span>
                  {item.isOnsite && (
                    <Tag
                      icon={<HomeOutlined />}
                      color="blue"
                      style={{ fontSize: 10, padding: '0 4px', lineHeight: '18px', margin: 0 }}
                    >
                      訪問/来社
                    </Tag>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                    {item.assignee}
                  </span>
                </div>
                {/* 2行目: 会社名 / 顧客名 */}
                <div style={{ fontSize: 13, color: '#262626', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.companyName}
                  <span style={{ color: '#8c8c8c', margin: '0 4px' }}>/</span>
                  {item.customerName}
                </div>
                {/* 3行目: 内容プレビュー */}
                <div style={{ fontSize: 12, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.content}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      </Modal>
    </>
  );
};

export default LinkFollowModal;
